import { ServiceVariationSelectField } from "@/services/backend/omni/servicevariation/components/service-variation-select";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInputField } from "@/shared/components/field/input-field";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { EditorInputField } from "@/shared/components/field/editor-input-field";
import {
  PatchRequest,
  updateOmniScopeOfWorkBaseDataCache,
  useOmniScopeOfWorkBaseDataPatchMutation,
  useOmniScopeOfWorkBaseDataShowQuery,
} from "../api/base-data-api";
import {
  ScopeOfWorkBaseDataPatch,
  patchScopeOfWorkBaseData,
} from "../types/base-data";

export function ScopeOfWorkBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading,
  } = useOmniScopeOfWorkBaseDataShowQuery({ id });
  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateOmniScopeOfWorkBaseDataCache,
    useOmniScopeOfWorkBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }
  if (isLoading || !data) {
    return <CardContentSkeleton />;
  }

  const patch = (p: ScopeOfWorkBaseDataPatch) =>
    set({ ...request, ...p }, patchScopeOfWorkBaseData(data, p));

  return (
    <div className="grid gap-4">
      {/* A rename to a taken label is a conflict, not a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => patch({ label })}
        fieldName="label"
      />
      <ServiceVariationSelectField
        serviceKind={data.serviceKind}
        value={data.serviceVariation}
        onValueChange={(serviceVariation) => patch({ serviceVariation })}
        fieldName="serviceVariation"
      />
      <TextInputField
        label={t("Titel")}
        text={data.title}
        onTextChange={(title) => patch({ title })}
        fieldName="title"
      />
      <TextInputField
        label={t("Untertitel")}
        text={data.subtitle}
        onTextChange={(subtitle) => patch({ subtitle })}
        fieldName="subtitle"
      />
      <EditorInputField
        label={t("Text")}
        initialHtml={data.text}
        onHtmlChange={(text: string) => patch({ text })}
        fieldName="text"
      />
    </div>
  );
}
